import React, { useState } from 'react';
import { useLanguage } from '../LanguageContext';
import { User } from '../types';
import './Profile.css';

interface ProfileProps {
  user: User;
  authToken: string;
  onUpdate: (user: User) => void;
  onBack: () => void;
}

export default function Profile({ user, authToken, onUpdate, onBack }: ProfileProps) {
  const { t, language, setLanguage } = useLanguage();
  const [nickname, setNickname] = useState(user.nickname || '');
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl || '');
  const [profileVisible, setProfileVisible] = useState(user.profileVisible !== false);
  const [socials, setSocials] = useState(user.socials || {});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          nickname,
          avatarUrl,
          profileVisible,
          socials,
          preferredLanguage: language,
        }),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        onUpdate(updatedUser);
        setMessage(t.common.success);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(t.common.error);
      }
    } catch (error) {
      setMessage(t.common.error);
    }
    setSaving(false);
  };

  const updateSocial = (key: string, value: string) => {
    setSocials(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="profile-container">
      <div className="profile-card">
        <button className="back-button" onClick={onBack}>
          ← {t.common.back}
        </button>

        <h1 className="profile-title">{t.profile.title}</h1>

        <div className="profile-avatar-section">
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" className="profile-avatar" />
          ) : (
            <div className="profile-avatar-placeholder">
              {(nickname || user.email)[0].toUpperCase()}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>{t.profile.nickname}</label>
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder={user.email.split('@')[0]}
          />
        </div>

        <div className="form-group">
          <label>{t.profile.avatar}</label>
          <input
            type="url"
            value={avatarUrl}
            onChange={(e) => setAvatarUrl(e.target.value)}
            placeholder="https://..."
          />
        </div>

        <div className="form-group">
          <label>{t.profile.language}</label>
          <div className="language-toggle">
            <button
              className={`lang-btn ${language === 'de' ? 'active' : ''}`}
              onClick={() => setLanguage('de')}
            >
              Deutsch
            </button>
            <button
              className={`lang-btn ${language === 'en' ? 'active' : ''}`}
              onClick={() => setLanguage('en')}
            >
              English
            </button>
          </div>
        </div>

        <div className="form-group visibility-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={profileVisible}
              onChange={(e) => setProfileVisible(e.target.checked)}
            />
            <span>{t.profile.visibility}</span>
          </label>
          <p className="form-hint">{t.profile.visibilityDesc}</p>
        </div>

        <div className="socials-section">
          <label>{t.profile.socials}</label>
          
          <div className="social-input">
            <span className="social-icon">📷</span>
            <input
              type="text"
              placeholder="Instagram"
              value={socials.instagram || ''}
              onChange={(e) => updateSocial('instagram', e.target.value)}
            />
          </div>

          <div className="social-input">
            <span className="social-icon">🐦</span>
            <input
              type="text"
              placeholder="Twitter / X"
              value={socials.twitter || ''}
              onChange={(e) => updateSocial('twitter', e.target.value)}
            />
          </div>

          <div className="social-input">
            <span className="social-icon">🎵</span>
            <input
              type="text"
              placeholder="TikTok"
              value={socials.tiktok || ''}
              onChange={(e) => updateSocial('tiktok', e.target.value)}
            />
          </div>

          <div className="social-input">
            <span className="social-icon">▶️</span>
            <input
              type="text"
              placeholder="YouTube"
              value={socials.youtube || ''}
              onChange={(e) => updateSocial('youtube', e.target.value)}
            />
          </div>

          <div className="social-input">
            <span className="social-icon">🎧</span>
            <input
              type="text"
              placeholder="Spotify"
              value={socials.spotify || ''}
              onChange={(e) => updateSocial('spotify', e.target.value)}
            />
          </div>

          <div className="social-input">
            <span className="social-icon">🌐</span>
            <input
              type="url"
              placeholder="Website"
              value={socials.website || ''}
              onChange={(e) => updateSocial('website', e.target.value)}
            />
          </div>
        </div>

        {message && (
          <div className={`message ${message === t.common.success ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        <button 
          className="btn btn-primary save-btn" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? t.common.loading : t.common.save}
        </button>
      </div>
    </div>
  );
}
